export type InAppInfo = {
	isInApp: boolean
	appName:
		| 'line' | 'telegram' | 'facebook' | 'instagram'
		| 'twitter' | 'tiktok' | 'wechat' | 'kakaotalk'
		| null

	// เพิ่มข้อมูลเบราว์เซอร์/แพลตฟอร์ม
	browser: 'safari' | 'chrome' | 'edge' | 'firefox' | 'opera' | 'brave' | 'unknown'
	isSafari: boolean
	isMobileSafari: boolean
	isChrome: boolean
	isIOSChrome: boolean
	isAndroidChrome: boolean

	// แพลตฟอร์ม
	isIOS: boolean
	isAndroid: boolean
	isMac: boolean
	isWindows: boolean
	isMobile: boolean
	isTablet: boolean

	// WKWebView ฝังในแอปบน iOS ที่ไม่ประกาศตัว (Telegram iOS เป็นตัวหลัก): UA ไม่มี
	// " safari/" และไม่ใช่ Chrome/Firefox/Edge บน iOS — Safari, SFSafariViewController
	// และ CriOS/FxiOS/EdgiOS ล้วนมี token ของตัวเอง จึงไม่ชนกับเบราว์เซอร์ปกติ
	isIOSWebView: boolean

	referrer: string
}

export function detectInApp(win: Window & typeof globalThis = window): InAppInfo {
	const nav = win.navigator as any
	const ua = (nav.userAgent || '').toLowerCase()
	const ref = document.referrer || ''

	/* ---------- in-app detection (ของเดิม) ---------- */
	const isLineUA      = ua.includes(' line/') || ua.includes(' line ')
	const isTelegramUA  = ua.includes('telegram')
	const isFBInAppUA   = ua.includes('fban') || ua.includes('fbav') || ua.includes('fb_iab')
	const isInstagramUA = ua.includes('instagram')
	const isTwitterUA   = ua.includes('twitter')
	const isTikTokUA    = ua.includes('tiktok')
	const isWeChatUA    = ua.includes('micromessenger')
	const isKakaoUA     = ua.includes('kakaotalk')

	const isGenericWebView =
		ua.includes('wv)') ||
		(ua.includes('version/') && ua.includes('mobile') && ua.includes('safari'))

	const isTelegramWebApp = !!(win as any).Telegram?.WebApp
	const hasLIFF          = typeof (win as any).liff !== 'undefined'
	const isLineRef        = /(^|\.)line\.me/i.test(ref)
	const isTelegramRef    = /(^|\.)t\.me/i.test(ref) || /telegram\.org/i.test(ref)

	let appName: InAppInfo['appName'] = null
	if (isTelegramWebApp) appName = 'telegram'
	else if (isLineUA || isLineRef || hasLIFF) appName = 'line'
	else if (isTelegramUA || isTelegramRef) appName = 'telegram'
	else if (isFBInAppUA) appName = 'facebook'
	else if (isInstagramUA) appName = 'instagram'
	else if (isTwitterUA) appName = 'twitter'
	else if (isTikTokUA) appName = 'tiktok'
	else if (isWeChatUA) appName = 'wechat'
	else if (isKakaoUA) appName = 'kakaotalk'
	const isInApp = !!appName || isGenericWebView

	/* ---------- platform detection ---------- */
	const isIOS     = /iphone|ipad|ipod/i.test(nav.platform || '') || /iphone|ipad|ipod/i.test(ua)
	const isAndroid = /android/i.test(ua)
	const isMac     = /macintosh|mac os x/i.test(ua) && !isIOS
	const isWindows = /windows/i.test(ua)
	const isMobile  = /mobile/i.test(ua) || isIOS || isAndroid
	const isTablet  = /ipad|tablet/i.test(ua)

	/* ---------- browser detection (UAData + fallback) ---------- */
	// 1) UA-CH (Chromium เท่านั้น)
	const brands: Array<{brand: string; version: string}> =
		nav.userAgentData?.brands || nav.userAgentData?.uaList || []

	const brandStr = brands.map(b => b.brand.toLowerCase()).join('|')
	const hasBrand = (name: string) => brandStr.includes(name)

	let browser: InAppInfo['browser'] = 'unknown'

	// Brave ไม่มี UA ชัดเจน ใช้การตรวจ property แบบคร่าว ๆ
	const isLikelyBrave = typeof (nav.brave)?.isBrave === 'function'

	// 2) heuristics จาก UA (รองรับ iOS tokens)
	const isEdgeToken    = ua.includes(' edg/') || ua.includes('edgios')
	const isFirefoxToken = ua.includes(' firefox/') || ua.includes('fxios')
	const isOperaToken   = ua.includes(' opr/') || ua.includes('opera')
	const isCriOS        = ua.includes(' crios/')   // Chrome on iOS
	const isFxiOS        = ua.includes(' fxios')    // Firefox on iOS
	const isEdgiOS       = ua.includes(' edgios')   // Edge on iOS
	const isChromiumLike = ua.includes(' chrome/') || ua.includes(' chromium/') || isCriOS || isEdgeToken || isOperaToken
	const isSafariToken  = ua.includes(' safari/') && ua.includes(' version/') && !isChromiumLike && !isFirefoxToken

	// resolve browser
	if (isLikelyBrave) browser = 'brave'
	else if (isEdgeToken || hasBrand('edge')) browser = 'edge'
	else if (isFirefoxToken || hasBrand('firefox')) browser = 'firefox'
	else if (isOperaToken || hasBrand('opera')) browser = 'opera'
	else if (isSafariToken || (!brands.length && !isChromiumLike && ua.includes('safari'))) browser = 'safari'
	else if (hasBrand('google chrome') || hasBrand('chromium') || hasBrand('chrome') || isChromiumLike) browser = 'chrome'

	// flags สะดวกใช้
	const isIOSChrome     = isCriOS
	const isAndroidChrome = isAndroid && browser === 'chrome'
	const isChrome        = browser === 'chrome'
	const isSafari        = browser === 'safari'
	const isMobileSafari  = isSafari && isIOS
	const isIOSWebView = isIOS && !ua.includes(' safari/') && !isCriOS && !isFxiOS && !isEdgiOS

	return {
		isInApp, appName, referrer: ref,
		browser, isSafari, isMobileSafari, isChrome, isIOSChrome, isAndroidChrome,
		isIOS, isAndroid, isMac, isWindows, isMobile, isTablet, isIOSWebView,
	}
}
